import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, firstValueFrom, map, of, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoggerService } from '../logger/logger.service';

const TAG = 'ConnectivityService';

/**
 * Linear retry spec (Figma: "Connection lost" / "No internet connection"
 * screens) — attempt count, interval, and the probe URL itself all come from
 * `environment.connectivityCheck` so any of the three can be changed per
 * environment (or swapped entirely, e.g. to a different endpoint) without
 * touching this service. `PROBE_TIMEOUT_MS` is a fixed implementation detail
 * of a single attempt (not part of the retry spec), so it stays a constant.
 */
const PROBE_TIMEOUT_MS = 3000;

export type ConnectivityStatus = 'online' | 'checking' | 'offline';

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * App-wide internet-connectivity watchdog behind the "Connection lost" / "No
 * internet connection" overlay (OfflineOverlayComponent). `status` drives which
 * screen (if any) is shown:
 *  - 'online'   -> overlay hidden, app behaves normally.
 *  - 'checking' -> "Connection lost" screen (spinner, reconnecting) — either the
 *                  initial detection or a manual Retry from the offline screen.
 *  - 'offline'  -> "No internet connection" screen (Retry button), reached once
 *                  all 3 linear attempts (1s apart) have failed.
 *
 * Real reachability, not just `navigator.onLine`: each attempt is a cache-busted
 * GET against `environment.connectivityCheck.probeUrl` — by default the same
 * lightweight, unauthenticated, cross-origin CDN app-config asset fetched
 * elsewhere in the app, so a device that's "online" per the OS but has no real
 * internet (e.g. a dead upstream link) is still correctly detected as offline.
 * The URL, attempt count, and retry interval are all environment-configurable
 * (see environment.ts) rather than hardcoded here.
 *
 * Triggers a check on:
 *  - the browser `offline` event (definite signal something's wrong);
 *  - the browser `online` event, but only to *verify* recovery when we're
 *    currently `checking`/`offline` — never trusted blindly;
 *  - any HTTP call failing with a network error (`ApiError.isNetworkError`,
 *    i.e. status 0), reported by `response.interceptor.ts` via
 *    {@link reportNetworkError};
 *  - the offline screen's own Retry button, via {@link retry}.
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityService implements OnDestroy {
  private http = inject(HttpClient);
  private logger = inject(LoggerService);

  readonly status = signal<ConnectivityStatus>('online');
  readonly isOffline = computed(() => this.status() !== 'online');

  private readonly handleOffline = () => this.startCheck('browser-offline-event');
  private readonly handleOnline = () => {
    // Never trust the browser's word alone — only re-verify if we currently
    // believe there's a problem; otherwise this is a no-op.
    if (this.status() !== 'online') this.startCheck('browser-online-event');
  };

  constructor() {
    window.addEventListener('offline', this.handleOffline);
    window.addEventListener('online', this.handleOnline);

    // The browser does not replay an `offline` event that happened before this
    // singleton was created. Start in the existing checking state so the overlay
    // can paint immediately, then retain the normal probe-based verdict.
    if (navigator.onLine === false) {
      this.logger.debug(TAG, 'Starting connectivity check sequence', { source: 'startup-offline' });
      this.status.set('checking');

      // ResponseInterceptor injects this service. Deferring HttpClient work
      // avoids re-entering Angular DI while that interceptor is being created.
      queueMicrotask(() => {
        if (this.status() === 'checking') void this.runProbeSequence();
      });
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('online', this.handleOnline);
  }

  /** Called by response.interceptor.ts whenever a request fails with ApiError.isNetworkError. */
  reportNetworkError(): void {
    this.startCheck('http-network-error');
  }

  /** "Retry" button on the "No internet connection" screen. */
  retry(): void {
    this.startCheck('manual-retry');
  }

  private startCheck(source: string): void {
    if (this.status() === 'checking') return; // a sequence is already running
    this.logger.debug(TAG, 'Starting connectivity check sequence', { source });
    this.status.set('checking');
    void this.runProbeSequence();
  }

  private async runProbeSequence(): Promise<void> {
    const { maxAttempts, retryIntervalMs } = environment.connectivityCheck;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const reachable = await this.probeOnce();
      if (reachable) {
        this.logger.debug(TAG, 'Connectivity restored', { attempt });
        this.status.set('online');
        return;
      }
      this.logger.debug(TAG, 'Connectivity probe failed', { attempt, maxAttempts });
      if (attempt < maxAttempts) await delay(retryIntervalMs);
    }
    this.logger.warn(TAG, 'Connectivity check exhausted all attempts — offline', {
      attempts: maxAttempts,
    });
    this.status.set('offline');
  }

  private probeOnce(): Promise<boolean> {
    const probeUrl = `${environment.connectivityCheck.probeUrl}?_cb=${Date.now()}`;
    return firstValueFrom(
      this.http.get(probeUrl, { responseType: 'text' }).pipe(
        timeout(PROBE_TIMEOUT_MS),
        map(() => true),
        catchError(() => of(false)),
      ),
    );
  }
}
